import React, { useState } from 'react';
import { Shield, Mail, Phone, Lock, Trash2, AlertTriangle, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useToast } from '../../hooks/use-toast';
import { updatePassword, updateEmail, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import UserProfileService from '../../services/userProfileService';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog';

const SecuritySection = () => {
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const [emailData, setEmailData] = useState({
    newEmail: '',
    password: ''
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [phoneNumber, setPhoneNumber] = useState('');
  const [isUpdatingPhone, setIsUpdatingPhone] = useState(false);

  // Charger le numéro de téléphone au montage
  React.useEffect(() => {
    const loadPhoneNumber = async () => {
      if (!user?.uid) return;
      
      try {
        const response = await UserProfileService.getUserProfile(user.uid);
        if (response.success && response.data) {
          setPhoneNumber(response.data.phone_number || '');
        }
      } catch (error) {
        console.error('Erreur chargement téléphone:', error);
      }
    };

    loadPhoneNumber();
  }, [user?.uid]);

  const handlePhoneUpdate = async () => {
    if (!user?.uid) return;
    
    setIsUpdatingPhone(true);
    try {
      const response = await UserProfileService.updateUserProfile(user.uid, {
        phone_number: phoneNumber
      });
      
      if (response.success) {
        toast({
          title: "Numéro mis à jour !",
          description: "Votre numéro de téléphone a été sauvegardé"
        });
      } else {
        throw new Error(response.message || 'Erreur mise à jour');
      }
    } catch (error) {
      console.error('Erreur mise à jour téléphone:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Erreur mise à jour téléphone';
      
      toast({
        title: "Erreur",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsUpdatingPhone(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleEmailChange = async (e) => {
    e.preventDefault();
    setIsUpdatingEmail(true);

    try {
      // Validation
      if (!emailData.newEmail) {
        throw new Error('Nouvel email requis');
      }
      if (!emailData.password) {
        throw new Error('Mot de passe requis pour confirmer');
      }

      // Ré-authentifier l'utilisateur avant de changer l'email
      const credential = EmailAuthProvider.credential(user.email, emailData.password);
      await reauthenticateWithCredential(user, credential);

      // Mettre à jour l'email Firebase
      await updateEmail(user, emailData.newEmail);

      toast({
        title: "Email mis à jour !",
        description: "Votre adresse email a été changée avec succès"
      });

      setEmailData({ newEmail: '', password: '' });
    } catch (error) {
      console.error('Erreur changement email:', error);
      let errorMessage = error.message;
      
      if (error.code === 'auth/wrong-password') {
        errorMessage = 'Mot de passe incorrect';
      } else if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'Cette adresse email est déjà utilisée';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Adresse email invalide';
      }

      toast({
        title: "Erreur",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsUpdatingEmail(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setIsChangingPassword(true);

    try {
      // Validations
      if (!passwordData.currentPassword) {
        throw new Error('Mot de passe actuel requis');
      }
      if (!passwordData.newPassword) {
        throw new Error('Nouveau mot de passe requis');
      }
      if (passwordData.newPassword !== passwordData.confirmPassword) {
        throw new Error('Les mots de passe ne correspondent pas');
      }
      if (passwordData.newPassword.length < 6) {
        throw new Error('Le mot de passe doit contenir au moins 6 caractères');
      }

      // Ré-authentifier l'utilisateur avant de changer le mot de passe
      const credential = EmailAuthProvider.credential(user.email, passwordData.currentPassword);
      await reauthenticateWithCredential(user, credential);

      // Mettre à jour le mot de passe Firebase
      await updatePassword(user, passwordData.newPassword);

      toast({
        title: "Mot de passe modifié !",
        description: "Votre mot de passe a été mis à jour avec succès"
      });

      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      console.error('Erreur changement mot de passe:', error);
      let errorMessage = error.message;
      
      if (error.code === 'auth/wrong-password') {
        errorMessage = 'Mot de passe actuel incorrect';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Le nouveau mot de passe est trop faible';
      }

      toast({
        title: "Erreur",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    // Afficher la modal de saisie du mot de passe
    setShowPasswordModal(true);
    setPasswordError('');
  };

  const proceedWithDeletion = async () => {
    if (!password.trim()) {
      setPasswordError('Le mot de passe est requis');
      return;
    }

    setIsDeletingAccount(true);
    setShowPasswordModal(false);

    try {
      if (!user?.uid) {
        throw new Error('Utilisateur non connecté');
      }

      console.log('🗑️ Début suppression complète compte utilisateur:', user.uid);

      // ÉTAPE 0: Ré-authentification OBLIGATOIRE (sécurité Firebase)
      try {
        console.log('🔐 Ré-authentification Firebase...');
        const credential = EmailAuthProvider.credential(user.email, password);
        await reauthenticateWithCredential(user, credential);
        console.log('✅ Ré-authentification réussie');
      } catch (reauthError) {
        console.error('❌ Erreur ré-authentification:', reauthError);
        if (reauthError.code === 'auth/wrong-password') {
          throw new Error('Mot de passe incorrect. Vérifiez vos identifiants.');
        } else if (reauthError.code === 'auth/too-many-requests') {
          throw new Error('Trop de tentatives. Veuillez réessayer plus tard.');
        } else {
          throw new Error('Erreur de ré-authentification: ' + reauthError.message);
        }
      }

      // ÉTAPE 1: Supprimer le profil de Supabase d'abord
      try {
        console.log('🗑️ Suppression données Supabase...');
        const supabaseResult = await UserProfileService.deleteUserProfile(user.uid);
        console.log('✅ Suppression Supabase terminée:', supabaseResult);
      } catch (supabaseError) {
        console.error('⚠️ Erreur suppression Supabase (continue avec Firebase):', supabaseError);
        // Continuer même si Supabase échoue pour supprimer Firebase
      }

      // ÉTAPE 2: Supprimer le compte Firebase (OBLIGATOIRE)
      try {
        console.log('🗑️ Suppression compte Firebase...');
        await user.delete();
        console.log('✅ Suppression Firebase terminée');
        
        toast({
          title: "Compte supprimé",
          description: "Votre compte et toutes vos données ont été définitivement supprimés (vidéos, images, séries, etc.)"
        });

        // La suppression Firebase déclenche automatiquement la déconnexion
        return; // Succès complet
        
      } catch (firebaseError) {
        console.error('❌ Erreur suppression Firebase:', firebaseError);
        throw firebaseError; // Faire remonter l'erreur Firebase
      }
    } catch (error) {
      console.error('❌ Erreur suppression compte:', error);
      let errorMessage = 'Impossible de supprimer le compte';
      
      if (error.code === 'auth/requires-recent-login') {
        errorMessage = 'Veuillez vous reconnecter avant de supprimer votre compte';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Mot de passe incorrect. Vérifiez vos identifiants.';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Trop de tentatives. Veuillez réessayer plus tard.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast({
        title: "Erreur de suppression",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsDeletingAccount(false);
      setPassword('');
    }
  };

  const proceedWithAccountDeletion = async () => {
    // ÉTAPE 1: Supprimer le profil de Supabase d'abord
    try {
      console.log('🗑️ Suppression données Supabase...');
      const supabaseResult = await UserProfileService.deleteUserProfile(user.uid);
      console.log('✅ Suppression Supabase terminée:', supabaseResult);
    } catch (supabaseError) {
      console.error('⚠️ Erreur suppression Supabase (continue avec Firebase):', supabaseError);
      // Continuer même si Supabase échoue pour supprimer Firebase
    }

    // ÉTAPE 2: Supprimer le compte Firebase (OBLIGATOIRE)
    try {
      console.log('🗑️ Suppression compte Firebase...');
      await user.delete();
      console.log('✅ Suppression Firebase terminée');
      
      toast({
        title: "Compte supprimé",
        description: "Votre compte et toutes vos données ont été définitivement supprimés (vidéos, images, séries, etc.)"
      });

      // La suppression Firebase déclenche automatiquement la déconnexion
      return; // Succès complet
      
    } catch (firebaseError) {
      console.error('❌ Erreur suppression Firebase:', firebaseError);
      throw firebaseError; // Faire remonter l'erreur Firebase
    }
  };



  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Sécurité</h1>
        <p className="text-gray-400">Gérez vos informations de sécurité et de connexion</p>
      </div>

      {/* Changement d'email */}
      <div className="bg-gradient-to-r from-gray-900/80 to-gray-800/80 backdrop-blur-lg border border-gray-700 rounded-2xl p-8">
        <h2 className="text-2xl font-semibold text-white mb-6 flex items-center space-x-3">
          <Mail className="h-6 w-6 text-blue-400" />
          <span>Adresse email</span>
        </h2>

        <div className="space-y-4 mb-6">
          <div>
            <Label className="text-white">Email actuel</Label>
            <div className="mt-1 p-3 bg-gray-800/50 rounded-lg border border-gray-600">
              <span className="text-gray-300">{user?.email}</span>
            </div>
          </div>
        </div>

        <form onSubmit={handleEmailChange} className="space-y-4">
          <div>
            <Label htmlFor="newEmail" className="text-white">Nouvel email</Label>
            <Input
              id="newEmail"
              type="email"
              value={emailData.newEmail}
              onChange={(e) => setEmailData(prev => ({...prev, newEmail: e.target.value}))}
              placeholder="nouveau@email.com"
              className="mt-1 bg-gray-800/50 border-gray-600 text-white"
            />
          </div>

          <div>
            <Label htmlFor="emailPassword" className="text-white">Mot de passe actuel</Label>
            <Input
              id="emailPassword"
              type="password"
              value={emailData.password}
              onChange={(e) => setEmailData(prev => ({...prev, password: e.target.value}))}
              placeholder="••••••••"
              className="mt-1 bg-gray-800/50 border-gray-600 text-white"
            />
          </div>

          <Button
            type="submit"
            disabled={isUpdatingEmail}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isUpdatingEmail ? (
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                <span>Mise à jour...</span>
              </div>
            ) : (
              'Modifier l\'email'
            )}
          </Button>
        </form>
      </div>

      {/* Changement de mot de passe */}
      <div className="bg-gradient-to-r from-gray-900/80 to-gray-800/80 backdrop-blur-lg border border-gray-700 rounded-2xl p-8">
        <h2 className="text-2xl font-semibold text-white mb-6 flex items-center space-x-3">
          <Lock className="h-6 w-6 text-green-400" />
          <span>Mot de passe</span>
        </h2>

        <form onSubmit={handlePasswordChange} className="space-y-4">
          <div>
            <Label htmlFor="currentPassword" className="text-white">Mot de passe actuel</Label>
            <div className="relative">
              <Input
                id="currentPassword"
                type={showPasswords.current ? "text" : "password"}
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData(prev => ({...prev, currentPassword: e.target.value}))}
                placeholder="••••••••"
                className="mt-1 bg-gray-800/50 border-gray-600 text-white pr-12"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('current')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300"
              >
                {showPasswords.current ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          <div>
            <Label htmlFor="newPassword" className="text-white">Nouveau mot de passe</Label>
            <div className="relative">
              <Input
                id="newPassword"
                type={showPasswords.new ? "text" : "password"}
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData(prev => ({...prev, newPassword: e.target.value}))}
                placeholder="••••••••"
                className="mt-1 bg-gray-800/50 border-gray-600 text-white pr-12"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('new')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300"
              >
                {showPasswords.new ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          <div>
            <Label htmlFor="confirmPassword" className="text-white">Confirmer le nouveau mot de passe</Label>
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showPasswords.confirm ? "text" : "password"}
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData(prev => ({...prev, confirmPassword: e.target.value}))}
                placeholder="••••••••"
                className="mt-1 bg-gray-800/50 border-gray-600 text-white pr-12"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('confirm')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300"
              >
                {showPasswords.confirm ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            disabled={isChangingPassword}
            className="bg-green-600 hover:bg-green-700"
          >
            {isChangingPassword ? (
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                <span>Modification...</span>
              </div>
            ) : (
              'Modifier le mot de passe'
            )}
          </Button>
        </form>
      </div>

      {/* Numéro de téléphone */}
      <div className="bg-gradient-to-r from-gray-900/80 to-gray-800/80 backdrop-blur-lg border border-gray-700 rounded-2xl p-8">
        <h2 className="text-2xl font-semibold text-white mb-6 flex items-center space-x-3">
          <Phone className="h-6 w-6 text-purple-400" />
          <span>Numéro de téléphone</span>
        </h2>

        <div className="space-y-4">
          <div>
            <Label htmlFor="phone" className="text-white">Numéro de téléphone</Label>
            <Input
              id="phone"
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="+33 6 12 34 56 78"
              className="mt-1 bg-gray-800/50 border-gray-600 text-white"
            />
          </div>
          
          <Button className="bg-purple-600 hover:bg-purple-700" onClick={handlePhoneUpdate} disabled={isUpdatingPhone}>
            {isUpdatingPhone ? (
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                <span>Mise à jour...</span>
              </div>
            ) : (
              'Ajouter / Modifier'
            )}
          </Button>
        </div>
      </div>

      {/* Zone de danger */}
      <div className="bg-gradient-to-r from-red-900/20 to-red-800/20 backdrop-blur-lg border border-red-700/50 rounded-2xl p-8">
        <h2 className="text-2xl font-semibold text-white mb-6 flex items-center space-x-3">
          <AlertTriangle className="h-6 w-6 text-red-400" />
          <span>Zone de danger</span>
        </h2>

        <p className="text-gray-300 mb-6">
          La suppression de votre compte est irréversible. Toutes vos données, vidéos et informations seront définitivement perdues.
        </p>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive" className="bg-red-600 hover:bg-red-700 flex items-center space-x-2">
              <Trash2 className="h-5 w-5" />
              <span>Supprimer mon compte</span>
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="bg-gray-900 border-gray-700">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white flex items-center space-x-2">
                <AlertTriangle className="h-6 w-6 text-red-400" />
                <span>Supprimer définitivement votre compte ?</span>
              </AlertDialogTitle>
              <AlertDialogDescription className="text-gray-300">
                <strong>⚠️ SUPPRESSION COMPLÈTE IRRÉVERSIBLE</strong>
                <br />
                Toutes vos données seront définitivement supprimées de tous nos systèmes :
                <ul className="mt-3 space-y-1 list-disc list-inside text-sm">
                  <li><strong>Supabase :</strong> Profil, vidéos, séries, saisons, épisodes, watchlist</li>
                  <li><strong>Cloudflare Stream :</strong> Toutes vos vidéos hébergées</li>
                  <li><strong>Cloudflare Images :</strong> Toutes vos miniatures et bannières</li>
                  <li><strong>Firebase Auth :</strong> Votre compte de connexion</li>
                </ul>
                <p className="mt-3 text-red-400 text-sm">
                  <strong>Aucune récupération possible après confirmation.</strong>
                </p>
                <p className="mt-2 text-yellow-400 text-sm">
                  🔐 <strong>Votre mot de passe sera demandé</strong> pour valider la suppression (sécurité Firebase).
                </p>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-gray-700 text-white hover:bg-gray-600">
                Annuler
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteAccount}
                disabled={isDeletingAccount}
                className="bg-red-600 hover:bg-red-700"
              >
                {isDeletingAccount ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    <span>Suppression...</span>
                  </div>
                ) : (
                  'Oui, supprimer définitivement'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Modal saisie mot de passe pour suppression */}
        <AlertDialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
          <AlertDialogContent className="bg-gray-900 border-gray-700">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white flex items-center space-x-2">
                <Lock className="h-6 w-6 text-yellow-400" />
                <span>Confirmation par mot de passe</span>
              </AlertDialogTitle>
              <AlertDialogDescription className="text-gray-300">
                🔐 <strong>Sécurité Firebase :</strong> Pour supprimer définitivement votre compte, entrez votre mot de passe actuel.
              </AlertDialogDescription>
            </AlertDialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="deletion-password" className="text-white">
                  Mot de passe actuel
                </Label>
                <Input
                  id="deletion-password"
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setPasswordError('');
                  }}
                  className="bg-gray-700 text-white border-gray-600"
                  placeholder="Entrez votre mot de passe"
                  onKeyPress={(e) => e.key === 'Enter' && proceedWithDeletion()}
                />
                {passwordError && (
                  <p className="text-red-400 text-sm mt-1">{passwordError}</p>
                )}
              </div>
            </div>
            
            <AlertDialogFooter>
              <AlertDialogCancel 
                className="bg-gray-700 text-white hover:bg-gray-600"
                onClick={() => {
                  setShowPasswordModal(false);
                  setPassword('');
                  setPasswordError('');
                }}
                disabled={isDeletingAccount}
              >
                Annuler
              </AlertDialogCancel>
              <Button
                onClick={proceedWithDeletion}
                disabled={!password.trim() || isDeletingAccount}
                className="bg-red-600 hover:bg-red-700 disabled:opacity-50"
              >
                {isDeletingAccount ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    <span>Suppression...</span>
                  </div>
                ) : (
                  'Confirmer et supprimer'
                )}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default SecuritySection;